/**
 * @file AutoDebugger.jsx
 * @description Dev-only floating debugger panel.
 *              - Collects all errors, warnings, network calls, perf issues & route changes
 *              - Draggable, resizable panel with filter/search/copy/export
 *              - Zero production footprint — returns null when !IS_DEV
 *
 * Keyboard shortcut: Ctrl+Shift+D (or Cmd+Shift+D) to toggle
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { subscribe, clear, EVENT_TYPES } from './debuggerStore';
import { useAutoDebugger } from './useAutoDebugger';

const IS_DEV = import.meta.env.DEV;

// ─── Constants ─────────────────────────────────────────────────────────────────
const SEVERITY_CONFIG = {
    critical: { color: '#ef4444', bg: '#450a0a', label: '💥 CRITICAL', dot: '#ef4444' },
    error: { color: '#f87171', bg: '#1c0505', label: '🔴 ERROR', dot: '#f87171' },
    warning: { color: '#fbbf24', bg: '#1c1300', label: '🟡 WARN', dot: '#fbbf24' },
    info: { color: '#60a5fa', bg: '#0c1426', label: '🔵 INFO', dot: '#60a5fa' },
};

const TYPE_ICONS = {
    [EVENT_TYPES.ERROR]: '🚨',
    [EVENT_TYPES.WARNING]: '⚠️',
    [EVENT_TYPES.NETWORK]: '🌐',
    [EVENT_TYPES.PERF]: '⚡',
    [EVENT_TYPES.REACT]: '⚛️',
    [EVENT_TYPES.CONSOLE]: '📝',
    [EVENT_TYPES.ROUTE]: '🔀',
};

const ALL_FILTER = 'all';

// ─── Sub-components ────────────────────────────────────────────────────────────
function SeverityBadge({ severity }) {
    const cfg = SEVERITY_CONFIG[severity] ?? SEVERITY_CONFIG.info;
    return (
        <span style={{
            display: 'inline-block', padding: '1px 6px', borderRadius: '4px',
            fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em',
            color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}33`,
            textTransform: 'uppercase', flexShrink: 0,
        }}>
            {cfg.label}
        </span>
    );
}

function TypeBadge({ type }) {
    const colors = {
        [EVENT_TYPES.ERROR]: '#ef444420',
        [EVENT_TYPES.WARNING]: '#f59e0b20',
        [EVENT_TYPES.NETWORK]: '#3b82f620',
        [EVENT_TYPES.PERF]: '#8b5cf620',
        [EVENT_TYPES.REACT]: '#06b6d420',
        [EVENT_TYPES.CONSOLE]: '#64748b20',
        [EVENT_TYPES.ROUTE]: '#10b98120',
    };
    return (
        <span style={{
            display: 'inline-block', padding: '1px 6px', borderRadius: '4px',
            fontSize: '0.6rem', fontWeight: 600,
            color: '#e2e8f0', background: colors[type] ?? '#ffffff10',
            flexShrink: 0,
        }}>
            {TYPE_ICONS[type] ?? '📌'} {type}
        </span>
    );
}

function LogEntry({ log, isSelected, onClick }) {
    const cfg = SEVERITY_CONFIG[log.severity] ?? SEVERITY_CONFIG.info;
    const time = new Date(log.ts).toLocaleTimeString('en-US', {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });

    return (
        <div
            onClick={onClick}
            style={{
                padding: '0.5rem 0.75rem',
                borderBottom: '1px solid #1e293b',
                cursor: 'pointer',
                background: isSelected ? '#1e293b' : 'transparent',
                borderLeft: `3px solid ${isSelected ? cfg.color : 'transparent'}`,
                transition: 'background 0.1s',
            }}
            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#0f172a'; }}
            onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{ color: '#64748b', fontSize: '0.65rem', flexShrink: 0 }}>{time}</span>
                <SeverityBadge severity={log.severity} />
                <TypeBadge type={log.type} />
            </div>
            <div style={{
                marginTop: '4px', fontSize: '0.75rem', color: '#cbd5e1',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                maxWidth: '100%',
            }}>
                {log.message}
            </div>
        </div>
    );
}

function LogDetail({ log }) {
    if (!log) {
        return (
            <div style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#475569', fontSize: '0.8rem', flexDirection: 'column', gap: '0.5rem',
            }}>
                <span style={{ fontSize: '2rem' }}>🔍</span>
                Select a log entry to inspect
            </div>
        );
    }

    const time = new Date(log.ts).toLocaleString('en-US', {
        dateStyle: 'short', timeStyle: 'medium',
    });

    const copy = () => navigator.clipboard?.writeText(JSON.stringify(log, null, 2));

    return (
        <div style={{ flex: 1, overflow: 'auto', padding: '1rem', fontSize: '0.78rem', color: '#cbd5e1' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <SeverityBadge severity={log.severity} />
                    <TypeBadge type={log.type} />
                </div>
                <button
                    onClick={copy}
                    title="Copy JSON"
                    style={{
                        padding: '3px 8px', borderRadius: '5px', border: '1px solid #334155',
                        background: '#1e293b', color: '#94a3b8', cursor: 'pointer', fontSize: '0.65rem',
                    }}
                >
                    📋 Copy
                </button>
            </div>

            <Field label="Time" value={time} />
            <Field label="Message" value={log.message} mono />

            {log.url && <Field label="URL" value={`${log.method} ${log.url}`} mono />}
            {log.status && <Field label="Status" value={`${log.status} (${log.elapsed}ms)`} />}
            {log.source && <Field label="Source" value={log.source} mono />}
            {log.path && <Field label="Route" value={log.path} mono />}
            {log.duration != null && <Field label="Duration" value={`${log.duration}ms`} />}

            {log.stack && (
                <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ color: '#64748b', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '4px' }}>Stack Trace</div>
                    <pre style={{
                        background: '#0f172a', borderRadius: '6px', padding: '0.75rem',
                        fontSize: '0.7rem', lineHeight: 1.6, overflowX: 'auto',
                        border: '1px solid #1e293b', color: '#fca5a5', margin: 0,
                    }}>
                        {log.stack}
                    </pre>
                </div>
            )}

            {log.componentStack && (
                <div>
                    <div style={{ color: '#64748b', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '4px' }}>Component Stack</div>
                    <pre style={{
                        background: '#0f172a', borderRadius: '6px', padding: '0.75rem',
                        fontSize: '0.7rem', lineHeight: 1.6, overflowX: 'auto',
                        border: '1px solid #1e293b', color: '#86efac', margin: 0,
                    }}>
                        {log.componentStack}
                    </pre>
                </div>
            )}
        </div>
    );
}

function Field({ label, value, mono }) {
    return (
        <div style={{ marginBottom: '0.5rem' }}>
            <div style={{ color: '#64748b', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</div>
            <div style={{
                color: '#e2e8f0', fontSize: '0.75rem', marginTop: '2px',
                fontFamily: mono ? '"JetBrains Mono", "Fira Code", monospace' : 'inherit',
                wordBreak: 'break-all',
            }}>
                {value}
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AutoDebugger() {
    // Wire up all global interceptors
    useAutoDebugger();

    const [logs, setLogs] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState(ALL_FILTER);
    const [severityFilter, setSeverityFilter] = useState(ALL_FILTER);
    const [selectedLog, setSelectedLog] = useState(null);
    const [badge, setBadge] = useState(0); // unseen errors count
    const [dragging, setDragging] = useState(false);
    const [panelPos, setPanelPos] = useState({ x: 24, y: 24 });
    const [panelSize, setPanelSize] = useState({ w: 780, h: 540 });

    const dragOffset = useRef({ x: 0, y: 0 });
    const panelRef = useRef(null);
    const listRef = useRef(null);

    // ── Subscribe to store
    useEffect(() => {
        return subscribe((newLogs) => {
            setLogs(newLogs);
            if (!isOpen) {
                const errorCount = newLogs.filter(
                    (l) => l.severity === 'critical' || l.severity === 'error'
                ).length;
                setBadge(errorCount);
            } else {
                // Panel is open — reset unseen badge immediately
                setBadge(0);
            }
        });
    }, [isOpen]);

    // badge is reset inside the subscribe callback when isOpen is true
    // (no setState inside effect needed)

    // ── Keyboard shortcut: Ctrl/Cmd + Shift + D
    useEffect(() => {
        const onKey = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'D') {
                e.preventDefault();
                setIsOpen((v) => !v);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // ── Drag handlers
    const onDragStart = useCallback((e) => {
        if (e.target.closest('[data-no-drag]')) return;
        setDragging(true);
        dragOffset.current = {
            x: e.clientX - panelPos.x,
            y: e.clientY - panelPos.y,
        };
    }, [panelPos]);

    useEffect(() => {
        if (!dragging) return;

        const onMove = (e) => {
            setPanelPos({
                x: Math.max(0, Math.min(e.clientX - dragOffset.current.x, window.innerWidth - panelSize.w)),
                y: Math.max(0, Math.min(e.clientY - dragOffset.current.y, window.innerHeight - 80)),
            });
        };
        const onUp = () => setDragging(false);

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [dragging, panelSize.w]);

    // ── Filtered logs
    const filteredLogs = logs.filter((l) => {
        if (typeFilter !== ALL_FILTER && l.type !== typeFilter) return false;
        if (severityFilter !== ALL_FILTER && l.severity !== severityFilter) return false;
        if (search && !l.message?.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    // ── Stats
    const stats = {
        errors: logs.filter((l) => l.severity === 'critical' || l.severity === 'error').length,
        warnings: logs.filter((l) => l.severity === 'warning').length,
        network: logs.filter((l) => l.type === EVENT_TYPES.NETWORK).length,
        perf: logs.filter((l) => l.type === EVENT_TYPES.PERF).length,
    };

    const exportLogs = () => {
        const blob = new Blob([JSON.stringify(filteredLogs, null, 2)], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `debug-${Date.now()}.json`;
        a.click();
    };

    if (!IS_DEV) return null;

    // ─── FAB trigger button ───────────────────────────────────────────────────
    if (!isOpen) {
        return (
            <button
                id="auto-debugger-fab"
                title="Open Auto Debugger (Ctrl+Shift+D)"
                onClick={() => setIsOpen(true)}
                style={{
                    position: 'fixed', bottom: 100, right: 24, zIndex: 99990,
                    width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer',
                    background: stats.errors > 0
                        ? 'linear-gradient(135deg, #ef4444, #b91c1c)'
                        : 'linear-gradient(135deg, #1d4ed8, #1e40af)',
                    color: '#fff', fontSize: '1.3rem',
                    boxShadow: stats.errors > 0
                        ? '0 0 0 3px #ef444440, 0 8px 24px #ef444440'
                        : '0 4px 20px rgba(29,78,216,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transformOrigin: 'center',
                    animation: stats.errors > 0 ? 'debugger-pulse 1.5s infinite' : 'none',
                }}
            >
                🐛
                {badge > 0 && (
                    <span style={{
                        position: 'absolute', top: -4, right: -4,
                        background: '#ef4444', color: '#fff', borderRadius: '50%',
                        width: 18, height: 18, fontSize: '0.6rem', fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '2px solid #0f172a',
                    }}>
                        {badge > 99 ? '99+' : badge}
                    </span>
                )}
                <style>{`
          @keyframes debugger-pulse {
            0%, 100% { box-shadow: 0 0 0 3px #ef444440, 0 8px 24px #ef444440; }
            50% { box-shadow: 0 0 0 8px #ef444420, 0 8px 32px #ef444460; }
          }
        `}</style>
            </button>
        );
    }

    // ─── Full panel ───────────────────────────────────────────────────────────
    const headerH = 44;
    const toolbarH = 80;
    const listW = isMinimized ? '100%' : '42%';

    return (
        <div
            ref={panelRef}
            style={{
                position: 'fixed',
                left: panelPos.x,
                top: panelPos.y,
                width: panelSize.w,
                height: isMinimized ? headerH : panelSize.h,
                zIndex: 99990,
                background: '#0f172a',
                borderRadius: '12px',
                boxShadow: '0 24px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.08)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: 'height 0.25s ease',
                fontFamily: '"JetBrains Mono", "Fira Code", ui-monospace, monospace',
                fontSize: '12px',
                userSelect: dragging ? 'none' : 'auto',
                resize: isMinimized ? 'none' : 'both',
                minWidth: 480,
                minHeight: 300,
            }}
        >
            {/* ── Title Bar (draggable) ── */}
            <div
                onMouseDown={onDragStart}
                style={{
                    height: headerH, flexShrink: 0,
                    display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px',
                    background: 'linear-gradient(90deg, #1e293b, #0f172a)',
                    borderBottom: '1px solid #1e293b',
                    cursor: dragging ? 'grabbing' : 'grab',
                }}
            >
                {/* Traffic lights */}
                <div data-no-drag style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setIsOpen(false)}
                        title="Close" style={circleBtn('#ef4444')}>✕</button>
                    <button onClick={() => setIsMinimized((v) => !v)}
                        title="Minimize" style={circleBtn('#fbbf24')}>─</button>
                    <button onClick={() => setPanelSize({ w: 960, h: 620 })}
                        title="Expand" style={circleBtn('#22c55e')}>⤢</button>
                </div>

                <span style={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                    🐛 Auto Debugger
                </span>

                {/* Stats pills */}
                <div data-no-drag style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                    {stats.errors > 0 && <Pill color="#ef4444" label={`${stats.errors} err`} />}
                    {stats.warnings > 0 && <Pill color="#fbbf24" label={`${stats.warnings} warn`} />}
                    <Pill color="#60a5fa" label={`${stats.network} net`} />
                    <Pill color="#a78bfa" label={`${stats.perf} perf`} />
                </div>

                {/* Ctrl+Shift+D hint */}
                <span style={{ color: '#475569', fontSize: '0.58rem', flexShrink: 0 }}>⌘⇧D</span>
            </div>

            {!isMinimized && (
                <>
                    {/* ── Toolbar ── */}
                    <div style={{
                        height: toolbarH, flexShrink: 0, padding: '0 12px',
                        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                        borderBottom: '1px solid #1e293b', background: '#0f172a',
                    }}>
                        {/* Search */}
                        <input
                            data-no-drag
                            type="text"
                            placeholder="🔍 Search logs..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={inputStyle}
                        />

                        {/* Type filter */}
                        <select data-no-drag value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={selectStyle}>
                            <option value={ALL_FILTER}>All Types</option>
                            {Object.values(EVENT_TYPES).map((t) => (
                                <option key={t} value={t}>{TYPE_ICONS[t]} {t}</option>
                            ))}
                        </select>

                        {/* Severity filter */}
                        <select data-no-drag value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} style={selectStyle}>
                            <option value={ALL_FILTER}>All Severity</option>
                            <option value="critical">💥 Critical</option>
                            <option value="error">🔴 Error</option>
                            <option value="warning">🟡 Warning</option>
                            <option value="info">🔵 Info</option>
                        </select>

                        <div data-no-drag style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                            <ToolBtn onClick={exportLogs} title="Export JSON">⬇ Export</ToolBtn>
                            <ToolBtn onClick={() => { clear(); setSelectedLog(null); }} title="Clear all logs">🗑 Clear</ToolBtn>
                        </div>
                    </div>

                    {/* ── Body: List + Detail ── */}
                    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                        {/* Log list */}
                        <div
                            ref={listRef}
                            style={{
                                width: listW, borderRight: '1px solid #1e293b',
                                overflowY: 'auto', flexShrink: 0,
                            }}
                        >
                            {filteredLogs.length === 0 ? (
                                <div style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                    justifyContent: 'center', height: '100%', color: '#475569',
                                    gap: 8, padding: '2rem',
                                }}>
                                    <span style={{ fontSize: '2rem' }}>✅</span>
                                    <span style={{ fontSize: '0.8rem' }}>No logs yet — looking clean!</span>
                                    <span style={{ fontSize: '0.65rem', color: '#334155', textAlign: 'center' }}>
                                        Errors, warnings, network calls & performance<br />events will appear here automatically.
                                    </span>
                                </div>
                            ) : (
                                filteredLogs.map((log) => (
                                    <LogEntry
                                        key={log.id}
                                        log={log}
                                        isSelected={selectedLog?.id === log.id}
                                        onClick={() => setSelectedLog(log)}
                                    />
                                ))
                            )}
                        </div>

                        {/* Detail pane */}
                        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                            <LogDetail log={selectedLog} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

// ─── Tiny helpers ─────────────────────────────────────────────────────────────
function circleBtn(color) {
    return {
        width: 14, height: 14, borderRadius: '50%', border: 'none',
        background: color, cursor: 'pointer', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontSize: '0.5rem', color: 'transparent', transition: 'color 0.1s',
        padding: 0, lineHeight: 1,
    };
}

function Pill({ color, label }) {
    return (
        <span style={{
            padding: '2px 7px', borderRadius: '999px', fontSize: '0.6rem',
            fontWeight: 700, color, background: `${color}20`,
            border: `1px solid ${color}40`,
        }}>
            {label}
        </span>
    );
}

function ToolBtn({ onClick, title, children }) {
    return (
        <button
            data-no-drag
            onClick={onClick}
            title={title}
            style={{
                padding: '4px 10px', borderRadius: '6px',
                border: '1px solid #334155', background: '#1e293b',
                color: '#94a3b8', cursor: 'pointer', fontSize: '0.7rem',
                fontWeight: 600, boxShadow: 'none',
                transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#334155'; e.currentTarget.style.color = '#e2e8f0'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#1e293b'; e.currentTarget.style.color = '#94a3b8'; }}
        >
            {children}
        </button>
    );
}

const inputStyle = {
    flex: '1 1 160px', padding: '5px 10px', borderRadius: '6px',
    border: '1px solid #334155', background: '#1e293b',
    color: '#e2e8f0', fontSize: '0.72rem', outline: 'none',
    fontFamily: 'inherit',
};

const selectStyle = {
    padding: '5px 8px', borderRadius: '6px',
    border: '1px solid #334155', background: '#1e293b',
    color: '#e2e8f0', fontSize: '0.72rem', cursor: 'pointer',
    outline: 'none', fontFamily: 'inherit',
};
